import { useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  type CompendiumDetail,
  type CompendiumEntry,
  type CompendiumType,
} from '../../api/client';
import {
  Button,
  Card,
  EmptyState,
  Input,
  MarkdownView,
  Spinner,
  StatBlock,
  Tabs,
} from '../ui';
import { importKindFor } from '../../lib/srdImport';
import styles from './CompendiumBrowser.module.css';

const TYPE_TABS: { id: CompendiumType; label: string; importable: boolean }[] = [
  { id: 'monsters', label: '🐉 Mostri', importable: true },
  { id: 'spells', label: '✨ Incantesimi', importable: false },
  { id: 'equipment', label: '🎒 Equipaggiamento', importable: true },
  { id: 'magic-items', label: '🔮 Oggetti magici', importable: true },
];

function str(d: CompendiumDetail, key: string): string | undefined {
  const v = d[key];
  return typeof v === 'string' ? v : undefined;
}

function num(d: CompendiumDetail, key: string): number | undefined {
  const v = d[key];
  return typeof v === 'number' ? v : undefined;
}

function descMarkdown(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter((v) => typeof v === 'string').join('\n\n');
  return '';
}

function acText(ac: unknown): string | undefined {
  if (typeof ac === 'number') return String(ac);
  if (Array.isArray(ac) && ac.length) {
    const first = ac[0] as Record<string, unknown>;
    if (typeof first?.value === 'number') return String(first.value);
  }
  return undefined;
}

function speedText(speed: unknown): string | undefined {
  if (!speed || typeof speed !== 'object') return undefined;
  return Object.entries(speed as Record<string, unknown>)
    .map(([k, v]) => (k === 'walk' ? String(v) : `${k} ${v}`))
    .join(', ');
}

/** Renders a SRD detail object as a readable reference panel. */
function DetailView({ type, detail }: { type: CompendiumType; detail: CompendiumDetail }) {
  if (type === 'monsters') {
    const chips: [string, string | undefined][] = [
      ['CA', acText(detail.armor_class)],
      ['PF', num(detail, 'hit_points') != null ? String(num(detail, 'hit_points')) : undefined],
      ['Vel.', speedText(detail.speed)],
      ['GS', detail.challenge_rating != null ? String(detail.challenge_rating) : undefined],
    ];
    const actions = Array.isArray(detail.actions) ? detail.actions : [];
    const traits = Array.isArray(detail.special_abilities) ? detail.special_abilities : [];
    return (
      <div className={styles.detailBody}>
        <div className={styles.subline}>
          {[str(detail, 'size'), str(detail, 'type'), str(detail, 'alignment')]
            .filter(Boolean)
            .join(', ')}
        </div>
        <div className={styles.chips}>
          {chips
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <span key={k} className={styles.chip}>
                {k} {v}
              </span>
            ))}
        </div>
        <StatBlock
          abilities={{
            strength: num(detail, 'strength'),
            dexterity: num(detail, 'dexterity'),
            constitution: num(detail, 'constitution'),
            intelligence: num(detail, 'intelligence'),
            wisdom: num(detail, 'wisdom'),
            charisma: num(detail, 'charisma'),
          }}
        />
        {traits.length > 0 && (
          <div className={styles.block}>
            <div className={styles.blockLabel}>Tratti</div>
            {traits.map((t, i) => {
              const a = t as Record<string, unknown>;
              return (
                <p key={i}>
                  <strong>{String(a.name)}.</strong> {String(a.desc)}
                </p>
              );
            })}
          </div>
        )}
        {actions.length > 0 && (
          <div className={styles.block}>
            <div className={styles.blockLabel}>Azioni</div>
            {actions.map((t, i) => {
              const a = t as Record<string, unknown>;
              return (
                <p key={i}>
                  <strong>{String(a.name)}.</strong> {String(a.desc)}
                </p>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === 'spells') {
    const level = num(detail, 'level');
    const school = (detail.school as Record<string, unknown> | undefined)?.name;
    const higher = descMarkdown(detail.higher_level);
    return (
      <div className={styles.detailBody}>
        <div className={styles.subline}>
          {level === 0 ? 'Trucchetto' : `Livello ${level}`}
          {school ? ` · ${String(school)}` : ''}
        </div>
        <div className={styles.chips}>
          {str(detail, 'casting_time') && (
            <span className={styles.chip}>Lancio: {str(detail, 'casting_time')}</span>
          )}
          {str(detail, 'range') && <span className={styles.chip}>Gittata: {str(detail, 'range')}</span>}
          {str(detail, 'duration') && (
            <span className={styles.chip}>Durata: {str(detail, 'duration')}</span>
          )}
        </div>
        <MarkdownView>{descMarkdown(detail.desc)}</MarkdownView>
        {higher && (
          <div className={styles.block}>
            <div className={styles.blockLabel}>Ai livelli superiori</div>
            <MarkdownView>{higher}</MarkdownView>
          </div>
        )}
      </div>
    );
  }

  // equipment / magic-items
  const category = (detail.equipment_category as Record<string, unknown> | undefined)?.name;
  const rarity = (detail.rarity as Record<string, unknown> | undefined)?.name;
  return (
    <div className={styles.detailBody}>
      <div className={styles.subline}>
        {[category, rarity].filter(Boolean).map(String).join(' · ')}
      </div>
      <MarkdownView>{descMarkdown(detail.desc)}</MarkdownView>
    </div>
  );
}

export interface CompendiumBrowserProps {
  /** Called when the user imports an entry. Receives the bucket kind and the SRD detail. */
  onImport: (kind: 'beast' | 'item', detail: CompendiumDetail, type: CompendiumType) => void;
  /** Optional note rendered under the header (e.g. where imports land). */
  hint?: string;
}

/** Searchable SRD reference browser with import-to-adventure support. */
export function CompendiumBrowser({ onImport, hint }: CompendiumBrowserProps) {
  const [type, setType] = useState<CompendiumType>('monsters');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CompendiumEntry[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CompendiumEntry | null>(null);
  const [detail, setDetail] = useState<CompendiumDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout>>();

  const importable = useMemo(
    () => TYPE_TABS.find((t) => t.id === type)?.importable ?? false,
    [type],
  );

  useEffect(() => {
    setListError(null);
    setLoadingList(true);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const res = await api.searchCompendium(type, query.trim());
        setResults(res.results);
      } catch (err) {
        setResults([]);
        setListError(err instanceof Error ? err.message : 'Ricerca non riuscita');
      } finally {
        setLoadingList(false);
      }
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [type, query]);

  const openEntry = async (entry: CompendiumEntry) => {
    setSelected(entry);
    setDetail(null);
    setLoadingDetail(true);
    try {
      setDetail(await api.getCompendiumEntry(type, entry.index));
    } catch {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <Tabs
        items={TYPE_TABS.map((t) => ({ id: t.id, label: t.label }))}
        active={type}
        onChange={(id) => {
          setType(id as CompendiumType);
          setSelected(null);
          setDetail(null);
        }}
      />
      {hint && <p className={styles.hint}>{hint}</p>}

      <div className={styles.layout}>
        <div className={styles.listCol}>
          <Input
            type="search"
            placeholder="Cerca nel compendio SRD…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {loadingList ? (
            <div className={styles.center}>
              <Spinner />
            </div>
          ) : listError ? (
            <EmptyState icon="📡" title="Compendio non disponibile" description={listError} />
          ) : results.length === 0 ? (
            <EmptyState icon="🔍" title="Nessun risultato" />
          ) : (
            <ul className={styles.list}>
              {results.map((entry) => (
                <li key={entry.index}>
                  <button
                    className={[
                      styles.listItem,
                      selected?.index === entry.index ? styles.listItemActive : undefined,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => void openEntry(entry)}
                  >
                    {entry.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.detailCol}>
          {!selected ? (
            <EmptyState icon="📖" title="Seleziona una voce" description="Sfoglia e importa nel contenuto dell'avventura." />
          ) : (
            <Card className={styles.detailCard}>
              <div className={styles.detailHead}>
                <h3 className={styles.detailTitle}>{selected.name}</h3>
                {importable && detail && (
                  <Button
                    variant="primary"
                    size="sm"
                    icon="＋"
                    onClick={() => onImport(importKindFor(type), detail, type)}
                  >
                    Importa
                  </Button>
                )}
              </div>
              {loadingDetail ? (
                <div className={styles.center}>
                  <Spinner />
                </div>
              ) : detail ? (
                <DetailView type={type} detail={detail} />
              ) : (
                <EmptyState icon="📡" title="Dettaglio non disponibile" />
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
